import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useAdminOverviewQuery, useCreateTenantMutation, type CreateTenantPayload } from "@/api/tenants";
import { useAuth } from "@/auth";
import { BrandMark, BrandName, Button, Ribbon, SectionHeader } from "@/components/ui";
import { CreateTenantForm } from "@/features/admin/CreateTenantForm";
import { ImgaCard } from "@/features/admin/ImgaCard";
import { TenantTable } from "@/features/admin/TenantTable";
import { UsagePanel } from "@/features/admin/UsagePanel";
import { WorkerHealth } from "@/features/admin/WorkerHealth";
import { t } from "@/i18n";

function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError && error.message) return error.message;
  return fallback;
}

/**
 * /admin — vdd_super, hafif ust bar ile TAM SAYFA (docs/faz5-arayuz-spec.md
 * §2: "hafif üst bar ile tam sayfa" — normal Layout/gezinme cubugu YOK,
 * bkz. App.tsx: bu rota RequireTenant govdesinin DISINDA, dogrudan
 * RequireSuper altinda). Tenant sarti yoktur — hic uyeligi olmayan bir
 * super de buraya ulasabilmeli (App.test.tsx bunu dogrular).
 */
export function Admin() {
  const { logout, switchTenant } = useAuth();
  const navigate = useNavigate();
  const overviewQuery = useAdminOverviewQuery();
  const createMutation = useCreateTenantMutation();
  const [switchingId, setSwitchingId] = useState<number | null>(null);
  const [createSuccess, setCreateSuccess] = useState(false);

  function handleCreate(payload: CreateTenantPayload) {
    setCreateSuccess(false);
    createMutation.mutate(payload, { onSuccess: () => setCreateSuccess(true) });
  }

  async function handleSwitch(tenantId: number) {
    setSwitchingId(tenantId);
    try {
      await switchTenant(tenantId);
      navigate("/ozet");
    } finally {
      setSwitchingId(null);
    }
  }

  return (
    <div className="min-h-screen bg-bg">
      <header className="flex items-center justify-between border-b-[1.5px] border-line bg-card px-4 py-3">
        <div className="flex items-center gap-2.5">
          <BrandMark size={36} />
          <div className="leading-tight">
            <BrandName className="text-base" />
            <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-muted">{t("admin.title")}</p>
          </div>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={() => void logout()}>
          {t("nav.logout")}
        </Button>
      </header>

      <main className="mx-auto max-w-5xl space-y-6 p-4">
        <CreateTenantForm
          onSubmit={handleCreate}
          isSubmitting={createMutation.isPending}
          error={createMutation.isError ? errorMessage(createMutation.error, t("admin.create.error")) : null}
          success={createSuccess}
        />

        <section className="space-y-2">
          <SectionHeader title={t("admin.tenants.title")} />
          {overviewQuery.isLoading && <p className="text-sm text-muted">{t("common.loading")}</p>}
          {overviewQuery.isError && <Ribbon tone="bad">{t("common.error")}</Ribbon>}
          {overviewQuery.data && (
            <>
              <WorkerHealth worker={overviewQuery.data.worker} />
              <TenantTable tenants={overviewQuery.data.tenants} onSwitch={handleSwitch} switchingId={switchingId} />
            </>
          )}
        </section>

        <UsagePanel month={currentMonth()} />

        <ImgaCard />
      </main>
    </div>
  );
}
